/**
 * Error tracking utility using Sentry
 *
 * Provides centralized error tracking and logging.
 * Only initializes if SENTRY_DSN is configured.
 */

import * as Sentry from "@sentry/react";
import { env } from "@/config/env";
import { logger } from "@/utils/logger";

const SENTRY_DSN = env.sentryDsn;
let isInitialized = false;

/**
 * Initialize Sentry error tracking
 */
export function initErrorTracking(): void {
  // Prevent multiple initializations
  if (isInitialized) {
    return;
  }

  // Check if Sentry is already initialized (e.g., by another script or HMR)
  try {
    const client = Sentry.getClient();
    if (client) {
      isInitialized = true;
      return;
    }
  } catch {
    // getClient might throw if not initialized, which is fine
  }

  // Don't initialize if no DSN configured
  if (!SENTRY_DSN) {
    logger.info("[Error Tracking] Sentry DSN not configured, skipping initialization");
    return;
  }

  // Don't initialize in development unless explicitly enabled
  if (env.isDevelopment && !env.enableSentryDev) {
    return;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: env.isProduction ? "production" : "development",
      // Setting this option to true will send default PII data to Sentry
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: env.isProduction ? 0.1 : 1.0, // 10% in production, 100% in dev
      // Session Replay - Reduced to avoid rate limiting
      replaysSessionSampleRate: 0.05, // 5% of sessions (reduced from 10%)
      replaysOnErrorSampleRate: 0.5, // 50% of sessions with errors (reduced from 100%)
      // Release tracking
      release: env.appVersion || undefined,
      // Filter out localhost errors in production
      beforeSend(event, _hint) {
        // Don't send errors from localhost in production
        if (env.isProduction && window.location.hostname === "localhost") {
          return null;
        }
        return event;
      },
    });
    isInitialized = true;
  } catch (error) {
    // If initialization fails (e.g., already initialized), mark as initialized to prevent retries
    logger.warn(
      "[Error Tracking] Sentry initialization failed, may already be initialized:",
      error
    );
    isInitialized = true;
  }
}

/**
 * Log an error to Sentry
 */
export function logError(error: Error, context?: Record<string, unknown>): void {
  if (!SENTRY_DSN) return;

  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Log a message to Sentry
 */
export function logMessage(
  message: string,
  level: "info" | "warning" | "error" = "info"
): void {
  if (!SENTRY_DSN) return;

  Sentry.captureMessage(message, level);
}

/**
 * Set user context for error tracking
 */
export function setUserContext(userId: string, email?: string, username?: string): void {
  if (!SENTRY_DSN) return;

  Sentry.setUser({
    id: userId,
    email,
    username,
  });
}

/**
 * Clear user context
 */
export function clearUserContext(): void {
  if (!SENTRY_DSN) return;

  Sentry.setUser(null);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  level?: "info" | "warning" | "error"
): void {
  if (!SENTRY_DSN) return;

  Sentry.addBreadcrumb({
    message,
    category,
    level,
  });
}
